import { Badge, Button, Card, Dropdown, Form, InputGroup, Modal } from 'react-bootstrap';
import { WithTooltip } from '../../components/WithTooltip/WithTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faICursor, faSave, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useDetectChanges } from '../../../hooks/useDetectChanges';
import React, { forwardRef, useEffect, useState } from 'react';
import { Copyable } from '../../components/Copyable/Copyable';
import { LanguagesBadge } from '../../components/LanguagesBadge/LanguagesBadge';
import { CountryFlag } from '../../components/CountryFlag/CountryFlag';
import { Plus } from '../../components/Plus/Plus';
import { LanguageCodeInput } from '../../components/LanguageCodeInput/LanguageCodeInput';
import { normalizeInterestKey } from './interestKey';

const AdminTagComponent = ({ tag, setLoading, onUpdate }) => {
  const { database, currentUser } = useFirebase();
  const detectChanges = useDetectChanges();
  const [editModalShow, setEditModalShow] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagRenameLoading, setTagRenameLoading] = useState(false);
  const [interestKey, setInterestKey] = useState('');

  const [editLanguage, setEditLanguage] = useState<string>(null);
  const [editLanguageEnabled, setEditLanguageEnabled] = useState(true);
  const [isLanguageCodeValid, setIsLanguageCodeValid] = useState(false);

  useEffect(() => {
    if (!editModalShow) setNewTag(tag.label);
  }, [editModalShow]);

  useEffect(() => {
    setNewTag(tag.label);
  }, [tag]);

  useEffect(() => {
    setInterestKey(tag.interestKey || '');
  }, [tag]);

  const normalizedInterestKey = normalizeInterestKey(interestKey);

  return (
    <>
      <Card bg={'dark'}>
        <Card.Header>
          <Card.Title className={'mb-0 d-flex align-items-baseline justify-content-between'}>
            <div>
              <span>{tag.label}</span>
            </div>

            <Dropdown drop={'left'}>
              <Dropdown.Toggle
                as={forwardRef(({ onClick }: any, _) => (
                  <WithTooltip text={'Etichetta'} big={false}>
                    <Button
                      size={'sm'}
                      className={'ml-1'}
                      variant={'outline-primary'}
                      onClick={(e) => {
                        e.preventDefault();
                        onClick(e);
                      }}
                    >
                      <FontAwesomeIcon icon={faICursor} fixedWidth={true} />
                      <LanguagesBadge className={'ml-1'} amount={Object.keys(tag.i18n || {}).length} />
                    </Button>
                  </WithTooltip>
                ))}
              />

              <Dropdown.Menu>
                {Object.entries<any>(tag.i18n || {})
                  .map(([k, v]) => [k, v['label']])
                  .filter(([, v]) => !!v)
                  .map(([language, value]) => {
                    return (
                      <Dropdown.Item
                        key={language}
                        onClick={() => {
                          setEditLanguage(language);
                          setEditLanguageEnabled(false);
                          setNewTag(value);
                          setEditModalShow(true);
                        }}
                      >
                        <CountryFlag language={language} withText={true} />
                      </Dropdown.Item>
                    );
                  })}
                <Dropdown.Item
                  className={'text-primary'}
                  onClick={() => {
                    setIsLanguageCodeValid(false);
                    setEditLanguage('');
                    setEditLanguageEnabled(true);
                    setNewTag('');
                    setEditModalShow(true);
                  }}
                >
                  <Plus /> Aggiungi lingua
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <div>
            ID: <Copyable text={tag.id} />
          </div>
          <div>
            <strong>Mostra come serie (categoria)?</strong>
            <WithTooltip text={'Clicca per cambiare'} big={false} placement={'right'}>
              <Button
                as={Badge}
                pill
                variant={tag.showAsSeries ? 'success' : 'secondary'}
                className={'ml-2'}
                onClick={() => {
                  database
                    .doc(`tags/${tag.id}`)
                    .set({ showAsSeries: !tag.showAsSeries, lastEditBy: currentUser.uid }, { merge: true })
                    .then(() => {
                      const updatedTag = { ...tag, showAsSeries: !tag.showAsSeries };
                      onUpdate(updatedTag);
                    });
                }}
              >
                <FontAwesomeIcon icon={tag.showAsSeries ? faCheckCircle : faTimesCircle} />
              </Button>
            </WithTooltip>
          </div>
          <div>
            <strong>Visibile per Odontoiatra?</strong>
            <WithTooltip text={'Clicca per cambiare'} big={false} placement={'right'}>
              <Button
                as={Badge}
                pill
                variant={tag.visibleFor?.odontoiatra ? 'success' : 'secondary'}
                className={'ml-2'}
                onClick={() => {
                  database
                    .doc(`tags/${tag.id}`)
                    .set({ visibleFor: { ...(tag.visibleFor || {}), odontoiatra: !tag.visibleFor?.odontoiatra }, lastEditBy: currentUser.uid }, { merge: true })
                    .then(() => {
                      const updatedTag = { 
                        ...tag, 
                        visibleFor: { ...(tag.visibleFor || {}), odontoiatra: !tag.visibleFor?.odontoiatra }
                      };
                      onUpdate(updatedTag);
                    });
                }}
              >
                <FontAwesomeIcon icon={tag.visibleFor?.odontoiatra ? faCheckCircle : faTimesCircle} />
              </Button>
            </WithTooltip>
          </div>
          <div>
            <strong>Visibile per ASO?</strong>
            <WithTooltip text={'Clicca per cambiare'} big={false} placement={'right'}>
              <Button
                as={Badge}
                pill
                variant={tag.visibleFor?.aso ? 'success' : 'secondary'}
                className={'ml-2'}
                onClick={() => {
                  database
                    .doc(`tags/${tag.id}`)
                    .set({ visibleFor: { ...(tag.visibleFor || {}), aso: !tag.visibleFor?.aso }, lastEditBy: currentUser.uid }, { merge: true })
                    .then(() => {
                      const updatedTag = { 
                        ...tag, 
                        visibleFor: { ...(tag.visibleFor || {}), aso: !tag.visibleFor?.aso }
                      };
                      onUpdate(updatedTag);
                    });
                }}
              >
                <FontAwesomeIcon icon={tag.visibleFor?.aso ? faCheckCircle : faTimesCircle} />
              </Button>
            </WithTooltip>
          </div>
          <div className={'d-flex align-items-center'}>
            <strong>Ordine:</strong>
            <InputGroup className={'ml-2'} style={{ maxWidth: '110px' }}>
              <Form.Control
                type={'number'}
                value={tag.order}
                size={'sm'}
                onChange={(e) => {
                  tag.order = e.target.value;
                  detectChanges();
                }}
              />
              <InputGroup.Append>
                <Button
                  variant={'primary'}
                  size={'sm'}
                  onClick={() => {
                    database
                      .doc(`tags/${tag.id}`)
                      .set({ order: +tag.order, lastEditBy: currentUser.uid }, { merge: true })
                      .then(() => {
                        const updatedTag = { ...tag, order: +tag.order };
                        onUpdate(updatedTag);
                      });
                  }}
                >
                  <FontAwesomeIcon icon={faSave} size={'sm'} />
                </Button>
              </InputGroup.Append>
            </InputGroup>
          </div>
          <div className={'mt-2 d-flex align-items-center'}>
            <strong>Interest Key:</strong>
            <InputGroup className={'ml-2'} style={{ maxWidth: '280px' }}>
              <Form.Control
                aria-label={'Interest Key'}
                value={interestKey}
                size={'sm'}
                placeholder={'TAG_IMPLANTOLOGIA'}
                onChange={(e) => setInterestKey(e.target.value)}
                onBlur={() => setInterestKey(normalizeInterestKey(interestKey))}
              />
              <InputGroup.Append>
                <Button
                  variant={'primary'}
                  size={'sm'}
                  aria-label={'Save interest key'}
                  disabled={!normalizedInterestKey}
                  onClick={() => {
                    if (!normalizedInterestKey) return;
                    database
                      .doc(`tags/${tag.id}`)
                      .set({ interestKey: normalizedInterestKey, lastEditBy: currentUser.uid }, { merge: true })
                      .then(() => {
                        setInterestKey(normalizedInterestKey);
                        const updatedTag = { ...tag, interestKey: normalizedInterestKey };
                        onUpdate(updatedTag);
                      });
                  }}
                >
                  <FontAwesomeIcon icon={faSave} size={'sm'} />
                </Button>
              </InputGroup.Append>
            </InputGroup>
          </div>
          <Form.Text className={'text-muted'}>Formato richiesto: `TAG_NOME_INTERESSE`.</Form.Text>
          <div className={'mt-3 d-flex justify-content-end'}>
            <Button
              as={Badge}
              variant={!!tag.image ? 'primary' : 'secondary'}
              onClick={() => {
                if (!tag.image) return;
                window.open(tag.image, '_blank');
              }}
            >
              {!!tag.image ? 'Mostra immagine' : 'Nessuna immagine'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal show={editModalShow} onHide={() => setEditModalShow(false)} backdrop={'static'} keyboard={false}>
        <Modal.Header>
          <Modal.Title>Modifica tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LanguageCodeInput
            value={editLanguage}
            onChange={(e) => setEditLanguage(e)}
            disabled={!editLanguageEnabled || tagRenameLoading}
            onErrorChange={(e) => setIsLanguageCodeValid(!e)}
            autoFocus={editLanguageEnabled}
          />
          <Form.Group>
            <Form.Label>Etichetta</Form.Label>
            <Form.Control disabled={tagRenameLoading} value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder={"Inserisci un'etichetta..."} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'secondary'} disabled={!newTag || tagRenameLoading || !isLanguageCodeValid} onClick={() => setEditModalShow(false)}>
            Annulla
          </Button>
          <Button
            variant={'primary'}
            disabled={tagRenameLoading || !newTag}
            onClick={() => {
              setTagRenameLoading(true);
              const objUpdate: any = { i18n: { [editLanguage]: { label: newTag } }, lastEditBy: currentUser.uid };
              if (editLanguage == 'it') objUpdate.label = newTag;

              database
                .doc(`tags/${tag.id}`)
                .set(objUpdate, { merge: true })
                .then(() => {
                  setEditModalShow(false);
                  const parsedTag = { 
                    ...tag, 
                    i18n: { ...tag.i18n, [editLanguage]: { label: newTag } },
                    ...(editLanguage === 'it' && { label: newTag })
                  };
                  onUpdate(parsedTag);
                })
                .catch(console.error)
                .finally(() => setTagRenameLoading(false));
            }}
          >
            Conferma
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export { AdminTagComponent as AdminTag };
